import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, Grid, InputAdornment } from '@material-ui/core';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { FormattedMessage, useIntl } from 'react-intl';
import DialogTemplate from 'components/shared/DialogTemplate';
import ExpansionSection from 'components/templates/ExpansionSection';
import { Input } from 'components/FormInputs';
import { validatePatched } from 'logic/cottage';
import { useFormik } from 'components/shared/Formik';
import useTextTransformBlur from 'hooks/useTextTransformBlur';
import { capitalizeFirst } from 'logic/textTransform';

const langs = {
  sv: <FormattedMessage id="Swedish" defaultMessage="Swedish" />,
  en: <FormattedMessage id="English" defaultMessage="English (not required)" />,
  de: <FormattedMessage id="German" defaultMessage="German (not required)" />,
};

const EditPricing = ({ isLoading, onSubmit, onClose, cottage }) => {
  const [expanded, setExpanded] = React.useState('sv');
  const intl = useIntl();
  const isSmall = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const formik = useFormik({
    initialValues: {
      ...cottage,
    },
    enableReinitialize: true,
    validate: validatePatched({ intl, dialog: 'pricing' }),
    validateOnBlur: true,
    validateOnChange: false,
    onSubmit: async (values) => {
      await onSubmit(values);
    },
  });

  const togglePanel = (lang) => setExpanded(lang === expanded ? null : lang);

  const { values, errors, handleSubmit, handleChange, submitForm } = formik;

  const handleBlur = useTextTransformBlur(formik, {
    pricefield_sv: capitalizeFirst,
    pricefield_en: capitalizeFirst,
    pricefield_de: capitalizeFirst,
  });

  const dirty =
    Object.keys(langs).some(
      (lang) => cottage[`pricefield_${lang}`] !== (values[`pricefield_${lang}`] || null),
    ) ||
    [
      'price_low_week',
      'price_low_day',
      'price_high_week',
      'price_high_day',
      'price_per_month',
    ].some((key) => cottage[key] !== (values[key] || null));

  return (
    <DialogTemplate
      title={
        <>
          <FormattedMessage id="Pricing" defaultMessage="Pricing" />
          &nbsp;
          <>
            #{cottage?.id}
            {!isSmall && <>{cottage?.title_sv ? <> - "{cottage?.title_sv}"</> : ''}</>}
          </>
        </>
      }
      onSave={submitForm}
      pristine={!dirty}
      onClose={onClose}
      isUpdating={isLoading}
    >
      <Box pb={4}>
        <form onSubmit={handleSubmit} noValidate>
          <Box boxShadow={3} px={2} py={3}>
            <Typography>
              <FormattedMessage id="cottages.price_per_week" defaultMessage="Price per week" />
            </Typography>
            <Grid container item spacing={2}>
              <Grid item xs={6}>
                <Input
                  name="price_low_week"
                  value={values.price_low_week}
                  type="number"
                  label={<FormattedMessage id="cottages.price_from" defaultMessage="From" />}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  errors={errors}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">SEK</InputAdornment>,
                    inputProps: { min: 1, max: 9999999 },
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <Input
                  name="price_high_week"
                  value={values.price_high_week}
                  type="number"
                  label={<FormattedMessage id="cottages.price_to" defaultMessage="To" />}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  errors={errors}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">SEK</InputAdornment>,
                    inputProps: { min: 1, max: 9999999 },
                  }}
                />
              </Grid>
            </Grid>
          </Box>

          <Box boxShadow={3} px={2} py={3}>
            <Typography>
              <FormattedMessage id="cottages.price_per_night" defaultMessage="Price per night" />
            </Typography>
            <Grid container item spacing={2}>
              <Grid item xs={6}>
                <Input
                  name="price_low_day"
                  value={values.price_low_day}
                  type="number"
                  label={<FormattedMessage id="cottages.price_from" defaultMessage="From" />}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  errors={errors}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">SEK</InputAdornment>,
                    inputProps: { min: 1, max: 9999999 },
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <Input
                  name="price_high_day"
                  value={values.price_high_day}
                  type="number"
                  label={<FormattedMessage id="cottages.price_to" defaultMessage="To" />}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  errors={errors}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">SEK</InputAdornment>,
                    inputProps: { min: 1, max: 9999999 },
                  }}
                />
              </Grid>
            </Grid>
          </Box>
          <Box boxShadow={3} px={2} py={3}>
            <Typography>
              <FormattedMessage id="Month" defaultMessage="Price per month" />
            </Typography>
            <Input
              name="price_per_month"
              value={values.price_per_month}
              type="number"
              label={null}
              onChange={handleChange}
              onBlur={handleBlur}
              errors={errors}
              InputProps={{
                endAdornment: <InputAdornment position="end">SEK</InputAdornment>,
                inputProps: { min: 1, max: 9999999 },
              }}
            />
          </Box>
          <Box boxShadow={3} px={2} py={3}>
            <Typography>
              <FormattedMessage id="cottages.detailed_pricing" defaultMessage="Detailed pricing" />
            </Typography>
            {Object.keys(langs).map((lang) => (
              <ExpansionSection
                key={lang}
                summary={langs[lang]}
                expanded={expanded === lang}
                divider
                onChange={() => togglePanel(lang)}
              >
                <Box flex={1}>
                  <Input
                    name={`pricefield_${lang}`}
                    value={values[`pricefield_${lang}`]}
                    label={null}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    errors={errors}
                    multiline
                    rows={8}
                    help={
                      <FormattedMessage
                        id="pricefield.help"
                        defaultMessage="Here you can write additional price information and specify prices for each individual period / week"
                      />
                    }
                  />
                </Box>
              </ExpansionSection>
            ))}
          </Box>
          {cottage.commission && (
            <Box mt={2}>
              <Typography>
                <FormattedMessage
                  id="cottages.prices.inclusive"
                  defaultMessage="Prices should be inclusive Stugknuten's fee"
                />
              </Typography>
            </Box>
          )}
        </form>
      </Box>
    </DialogTemplate>
  );
};

EditPricing.propTypes = {
  isLoading: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  cottage: PropTypes.shape({
    id: PropTypes.number,
    title_sv: PropTypes.string,
    commission: PropTypes.bool,
  }),
};

export default EditPricing;
